import { useId, useRef, useState, type ReactNode } from "react";
import type { WhatsAppChannelCode, WhatsAppChannelsSettings, WhatsAppSenderPerson, WhatsAppSendersResponse } from "@gioia365/contracts";
import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { ApiRequestError } from "../../api/http";
import { useTab } from "../../api/queries";
import { AboutBox } from "../../design/components/AboutBox";
import { Button } from "../../design/components/Button";
import { Chip } from "../../design/components/Chip";
import { DataTable } from "../../design/components/DataTable";
import { Icon } from "../../design/components/Icon";
import { Reason } from "../../design/components/Reason";
import { SettingsLayout } from "../../design/components/SettingsLayout";
import { StatusPill } from "../../design/components/StatusPill";
import { useToast } from "../../design/components/Toast";
import { messageForError } from "../../i18n/errors";
import { useT } from "../../i18n/locale";
import { useAccess } from "../../session/access";
import type { TabContext } from "../../session/tab-context";
import { whatsAppTemplatesQuery } from "../../whatsapp/api";
import { useSaveWhatsAppChannels, whatsAppChannelsQuery, whatsAppSendersQuery } from "../../whatsapp/settings-api";
import type { Feedback } from "./draft-dialogs";
import { AgileDownNotice } from "./whatsapp-agile";
import { MetaBusinessPanel } from "./whatsapp-meta";
import { channelOf, ChannelText, inRelease, SenderNumber, SenderState, WHATSAPP_CHANNEL_ORDER, WhatsAppSenderDialog } from "./whatsapp-sender";
import "./whatsapp-settings.css";

// Impostazioni → «Invio dei WhatsApp» (piano M6, R1, Task 5a; D2; questioni 391, 403, 404 e 405; mock-up `whatsapp.html` e
// `mockup.js`, `initWaChannel`). Due livelli, come ha deciso il product owner il 5 ottobre 2026:
// - in cima «I WhatsApp che usa {azienda}» (`GET`/`PUT /api/settings/whatsapp-channels`): le tre caselle con i loro segni; «A mano»
//   sempre accesa (questione 391). Le cambia soltanto chi ha `settings.whatsapp_channels` (`canEdit` del server: fra i ruoli di
//   partenza il Super admin, e il Super admin di Gioia365; questione 403); gli altri le vedono spente con chi decide. Un WhatsApp
//   che il server non può usare resta spento con il perché e non si accende; già acceso, si può soltanto spegnere, e chi lo usava
//   passa a mano (WA-2). Spenta e senza poterla riaccendere, la casella non si raggiunge più: il focus va al titolo della sezione. Un
//   WhatsApp che non c'è nel rilascio direbbe «Arriva con il prossimo rilascio» (WA-2): da R9 ci sono tutti e tre. La versione è quella
//   delle impostazioni dell'azienda, la stessa del calendario e della sequenza: un `409 version_conflict` si dice e la scelta salvata
//   si rilegge;
// - sotto «Il WhatsApp di ogni Commerciale» (`GET /api/whatsapp/senders`): Commerciale | WhatsApp | Numero | Stato, e «Imposta» per
//   chi il server lascia impostare (`canSet`: Super user, Admin e Super admin su chi ha un ruolo più basso, il Super admin e Gioia365
//   su tutti, questione 405), con la finestra di `whatsapp-sender.tsx`. Chi non può impostare nessuno (`canEdit` falso) la vede in
//   sola lettura, con il motivo del mock-up e senza la colonna dell'azione.
// Da R2 (Task 7b) il WhatsApp ufficiale di Meta si accende per l'azienda quando le quattro variabili di Meta ci sono sul server
// (`metaSignup.available`); senza, la casella è spenta con «Il WhatsApp ufficiale non è ancora collegato a Gioia365» (accesa da
// prima, si può soltanto spegnere). Da R9 (Task 27b) WhatsApp Agile si accende quando la sua chiave c'è sul server
// (`agileSignup.available`); senza, la casella è spenta con «Non disponibile: WhatsApp Agile non è ancora collegato a Gioia365» (accesa da
// prima, si può soltanto spegnere, e chi lo usava passa a mano, WA-2); in cima alla pagina l'avviso quando il canale di Agile di
// qualcuno non consegna (`AgileDownNotice`, dall'ultimo controllo salvato). In fondo, con il WhatsApp ufficiale acceso e
// collegato, il riquadro «Business Manager di Meta» del mock-up (questione 416, `whatsapp-meta.tsx`), con «Modelli approvati da Meta:
// N di 6» dai modelli dell'azienda (`GET /api/whatsapp/templates`, letti soltanto quando il riquadro c'è; Task 8b).

export function WhatsAppSendingPage() {
  const t = useT();
  return (
    <SettingsLayout section="whatsappSending" title={t("whatsappSendingSettings.title")}>
      <WhatsAppSendingSection />
    </SettingsLayout>
  );
}

function WhatsAppSendingSection() {
  const t = useT();
  const tab = useTab();
  const access = useAccess();
  const toast = useToast();
  const peopleId = useId();
  const organization = access.context.organization.name;
  const senders = useQuery(whatsAppSendersQuery(tab));
  const channels = useQuery(whatsAppChannelsQuery(tab));
  const metaShown = channels.data?.meta === true && channels.data.metaSignup.available;
  const templates = useQuery({ ...whatsAppTemplatesQuery(tab), enabled: metaShown });
  const approved = templates.data ? { approved: templates.data.templates.filter((template) => template.approved).length, total: templates.data.templates.length } : null;
  // La finestra «Imposta», con la persona com'era all'apertura.
  const [setup, setSetup] = useState<WhatsAppSenderPerson | null>(null);
  const data = senders.data;

  let table: ReactNode;
  if (data) {
    table = <PeopleTable data={data} self={access.context.membershipId} onSetup={setSetup} />;
  } else if (senders.isError) {
    table = (
      <div className="g-wa-problem">
        <Reason tone="warning" role="alert">
          {messageForError(t, senders.error)}
        </Reason>
        <Button onClick={() => void senders.refetch()} disabled={senders.isFetching} aria-busy={senders.isFetching ? "true" : undefined}>
          {t("common.retry")}
        </Button>
      </div>
    );
  } else {
    table = <p className="g-wa-muted">{t("common.loading")}</p>;
  }

  return (
    <>
      <AboutBox title={t("whatsappSendingSettings.about")}>
        <p>{t("whatsappSendingSettings.aboutText")}</p>
        <h2>{t("whatsappSendingSettings.whoDecidesTitle")}</h2>
        <p>{t("whatsappSendingSettings.whoDecides")}</p>
      </AboutBox>
      {data && !data.canEdit ? <Reason>{t("whatsappSendingSettings.readOnly")}</Reason> : null}
      {data ? <AgileDownNotice people={data.people} /> : null}
      <CompanyChannels tab={tab} organization={organization} channels={channels} people={data?.people ?? null} />
      <section className="g-wa-section" aria-labelledby={peopleId}>
        <h2 id={peopleId}>{t("whatsappSendingSettings.people.title")}</h2>
        <p className="g-wa-muted">{t("whatsappSendingSettings.people.text", { organization })}</p>
        {table}
        <p className="g-wa-auto">
          <Icon name="info" />
          <span>
            <strong>{t("whatsappSendingSettings.people.autoTitle")}</strong> {t("whatsappSendingSettings.people.autoText")}
          </span>
        </p>
      </section>
      {metaShown ? <MetaBusinessPanel connection={channels.data?.metaSignup.connection ?? null} templates={approved} /> : null}
      {setup ? (
        <WhatsAppSenderDialog
          tab={tab}
          person={setup}
          onClose={() => setSetup(null)}
          onSaved={(message) => {
            setSetup(null);
            toast.show(message, "success");
          }}
        />
      ) : null}
    </>
  );
}

type PeopleTableProps = { data: WhatsAppSendersResponse; self: string | null; onSetup: (person: WhatsAppSenderPerson) => void };

/** «Il WhatsApp di ogni Commerciale»: una riga per persona non archiviata, in ordine di nome come la dà il server. */
function PeopleTable({ data, self, onSetup }: PeopleTableProps) {
  const t = useT();
  return (
    <DataTable caption={t("whatsappSendingSettings.people.caption")} captionHidden>
      <thead>
        <tr>
          <th scope="col">{t("whatsappSendingSettings.people.columns.person")}</th>
          <th scope="col">{t("whatsappSendingSettings.people.columns.channel")}</th>
          <th scope="col">{t("whatsappSendingSettings.people.columns.number")}</th>
          <th scope="col">{t("whatsappSendingSettings.people.columns.state")}</th>
          {data.canEdit ? (
            <th scope="col">
              <span className="g-visually-hidden">{t("whatsappSendingSettings.people.columns.action")}</span>
            </th>
          ) : null}
        </tr>
      </thead>
      <tbody>
        {data.people.map((person) => (
          <tr key={person.membershipId}>
            <th scope="row">
              <span className="g-wa-who">
                <strong>{person.displayName}</strong>
                {person.membershipId === self ? <Chip tone="accent">{t("whatsappSendingSettings.people.you")}</Chip> : null}
                {person.status === "suspended" ? (
                  <StatusPill tone="danger" icon="minus">
                    {t("team.status.suspended")}
                  </StatusPill>
                ) : null}
              </span>
            </th>
            <td>{t(`whatsappSendingSettings.channels.${channelOf(person)}.label`)}</td>
            <td>
              <SenderNumber sender={person.sender} />
            </td>
            <td>
              <SenderState sender={person.sender} />
            </td>
            {data.canEdit ? (
              <td>
                {person.canSet ? (
                  <Button onClick={() => onSetup(person)} aria-label={t("whatsappSendingSettings.people.setupNamed", { name: person.displayName })}>
                    {t("whatsappSendingSettings.people.setup")}
                  </Button>
                ) : null}
              </td>
            ) : null}
          </tr>
        ))}
      </tbody>
    </DataTable>
  );
}

type CompanyProps = {
  tab: TabContext;
  organization: string;
  channels: UseQueryResult<WhatsAppChannelsSettings>;
  /** Le persone con il loro WhatsApp, per dire quante passano a mano; `null` finché non sono arrivate. */
  people: readonly WhatsAppSenderPerson[] | null;
};

/** «I WhatsApp che usa {azienda}»: le tre caselle; «A mano» sempre accesa. */
function CompanyChannels({ tab, organization, channels, people }: CompanyProps) {
  const t = useT();
  const toast = useToast();
  const ids = useId();
  const titleRef = useRef<HTMLHeadingElement>(null);
  const save = useSaveWhatsAppChannels(tab);
  const [problem, setProblem] = useState<Feedback | null>(null);
  const data = channels.data;
  const canEdit = data?.canEdit === true;
  const busy = save.isPending;

  /** Se un WhatsApp automatico si può accendere: esiste in questo rilascio e il server lo può usare (Meta: le variabili; Agile: la chiave). */
  const canSwitchOn = (channel: Exclude<WhatsAppChannelCode, "manual">) =>
    inRelease(channel) && (channel === "meta" ? data?.metaSignup.available === true : data?.agileSignup.available === true);

  /** Accende un WhatsApp automatico per l'azienda: da quel momento si sceglie per ogni persona con «Imposta». */
  async function switchOn(channel: Exclude<WhatsAppChannelCode, "manual">) {
    if (!data || busy || data[channel] || !canSwitchOn(channel)) return;
    setProblem(null);
    try {
      await save.mutateAsync({ meta: channel === "meta" ? true : data.meta, agile: channel === "agile" ? true : data.agile, expectedVersion: data.version });
      toast.show(t("whatsappSendingSettings.company.switchedOn", { channel: t(`whatsappSendingSettings.channels.${channel}.label`), organization }), "success");
    } catch (error) {
      if (error instanceof ApiRequestError && error.code === "version_conflict") setProblem({ text: (tr) => tr("whatsappSendingSettings.company.conflict") });
      else setProblem({ text: (tr) => messageForError(tr, error) });
    }
  }

  /** Spegne un WhatsApp automatico: chi lo usava passa a mano (lo fa il server), e l'avviso dice quanti. */
  async function switchOff(channel: Exclude<WhatsAppChannelCode, "manual">) {
    if (!data || busy || !data[channel]) return;
    setProblem(null);
    const moved = people?.filter((person) => channelOf(person) === channel).length ?? 0;
    try {
      await save.mutateAsync({ meta: channel === "meta" ? false : data.meta, agile: channel === "agile" ? false : data.agile, expectedVersion: data.version });
      // Una casella appena spenta che non si riaccende (senza le variabili di Meta o la chiave di Agile): il focus non resta su un
      // controllo spento.
      if (!canSwitchOn(channel)) titleRef.current?.focus();
      const label = t(`whatsappSendingSettings.channels.${channel}.label`);
      if (moved === 0) toast.show(t("whatsappSendingSettings.company.switchedOff", { channel: label, organization }), "success");
      else if (moved === 1) toast.show(t("whatsappSendingSettings.company.switchedOffOne", { channel: label, organization }), "success");
      else toast.show(t("whatsappSendingSettings.company.switchedOffMany", { channel: label, organization, count: moved }), "success");
    } catch (error) {
      // La scelta si rilegge (`useSaveWhatsAppChannels`): le caselle mostrano quella salvata.
      if (error instanceof ApiRequestError && error.code === "version_conflict") setProblem({ text: (tr) => tr("whatsappSendingSettings.company.conflict") });
      else setProblem({ text: (tr) => messageForError(tr, error) });
    }
  }

  let body: ReactNode;
  if (data) {
    body = (
      <ul className="g-wa-kinds">
        {WHATSAPP_CHANNEL_ORDER.map((channel) => {
          const id = `${ids}-${channel}`;
          if (channel === "manual") {
            return (
              <li key={channel}>
                <label className="g-wa-mode" data-disabled="true">
                  <input type="checkbox" checked disabled readOnly aria-labelledby={`${id}-name`} aria-describedby={`${id}-text`} />
                  <ChannelText
                    id={id}
                    channel={channel}
                    why={null}
                    text={t("whatsappSendingSettings.channels.manual.text")}
                    extra={t("whatsappSendingSettings.company.always")}
                  />
                </label>
              </li>
            );
          }
          const available = canSwitchOn(channel);
          const on = data[channel];
          // Il WhatsApp ufficiale soltanto con le variabili di Meta, Agile soltanto con la sua chiave. Uno già acceso si può spegnere;
          // senza quello che serve sul server dice sempre perché non si riaccende.
          const enabled = canEdit && !busy && (available || on);
          let why: string | null = null;
          if (channel === "meta" && !data.metaSignup.available) why = t("whatsappSendingSettings.company.metaUnavailable");
          else if (channel === "agile" && !data.agileSignup.available) why = t("whatsappSendingSettings.company.agileUnavailable");
          else if (!available && !on) why = t("whatsappSendingSettings.company.nextRelease");
          return (
            <li key={channel}>
              <label className="g-wa-mode" data-disabled={enabled ? undefined : "true"}>
                <input
                  type="checkbox"
                  checked={on}
                  disabled={!enabled}
                  onChange={(event) => {
                    if (event.target.checked) void switchOn(channel);
                    else void switchOff(channel);
                  }}
                  aria-labelledby={`${id}-name`}
                  aria-describedby={why ? `${id}-why ${id}-text` : `${id}-text`}
                />
                <ChannelText id={id} channel={channel} why={why} text={t(`whatsappSendingSettings.channels.${channel}.text`)} />
              </label>
            </li>
          );
        })}
      </ul>
    );
  } else if (channels.isError) {
    body = (
      <div className="g-wa-problem">
        <Reason tone="warning" role="alert">
          {messageForError(t, channels.error)}
        </Reason>
        <Button onClick={() => void channels.refetch()} disabled={channels.isFetching} aria-busy={channels.isFetching ? "true" : undefined}>
          {t("common.retry")}
        </Button>
      </div>
    );
  } else {
    body = <p className="g-wa-muted">{t("common.loading")}</p>;
  }

  return (
    <section className="g-wa-section" aria-labelledby={`${ids}-title`}>
      <h2 id={`${ids}-title`} ref={titleRef} tabIndex={-1}>
        {t("whatsappSendingSettings.company.title", { organization })}
      </h2>
      <p className="g-wa-muted">
        {t("whatsappSendingSettings.company.text")}
        {data && !canEdit ? (
          <>
            {" "}
            {t("whatsappSendingSettings.company.readOnly", { organization })}
          </>
        ) : null}
      </p>
      {problem ? (
        <Reason tone="warning" role="alert">
          {problem.text(t)}
        </Reason>
      ) : null}
      {body}
    </section>
  );
}
